<template>
    <v-card
        :loading="loading"
    >
        <template
            v-slot:progress
        >
            <v-progress-linear
                color="primary"
                height="10"
                indeterminate
            />
        </template>
        <v-card-title
            class="text-subtitle-1 text-uppercase"
        >
            {{ $t(`rula.category.${category.category}`) }}
        </v-card-title>
        <v-card-text>
            <v-row>
                <v-col>
                    <v-checkbox
                        :disabled="!canEdit || notAvailable"
                        v-model="onSite"
                        :label="$t('rula.label.onSite')"
                    />
                    <v-checkbox
                        :disabled="!canEdit || onSite"
                        v-model="notAvailable"
                        :label="$t('rula.label.notAvailable')"
                    />
                </v-col>
            </v-row>
            <v-row
                v-if="!notAvailable && !onSite"
            >
                <v-col>
                    <v-data-table
                        :items="category.files"
                        :headers="fileHeaders"
                    >
                        <template
                            v-slot:item.fileOrUrl="{ item }"
                        >
                            <a
                                v-if="item.url"
                                :href="item.url"
                                target="_blank"
                            >
                                {{ $t('rula.label.link') }}
                            </a>
                            <a
                                v-if="item.file"
                                :href="item.file.src"
                                target="_blank"
                                download
                            >
                                {{ item.file.filename }}
                            </a>
                        </template>
                        <template
                            v-slot:item._actions="{ item }"
                        >
                            <v-btn
                                icon
                                v-show="canEdit"
                                @click="deleteItem(item)"
                            >
                                <v-icon>
                                    mdi-delete
                                </v-icon>
                            </v-btn>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
            <v-btn
                v-show="canEdit && !notAvailable && !onSite"
                color="secondary"
                fab
                small
                absolute
                bottom
                left
                :title="$t('rula.addMultiple')"
                @click="multipleDialog = true"
            >
                <v-icon>mdi-plus-box-multiple</v-icon>
            </v-btn>
            <v-btn
                v-show="canEdit && !notAvailable && !onSite"
                color="secondary"
                fab
                small
                absolute
                bottom
                left
                :title="$t('rula.add')"
                @click="newDialog = true"
                class="ml-16"
            >
                <v-icon>mdi-plus</v-icon>
            </v-btn>
            <v-dialog
                v-model="multipleDialog"
                persistent
                max-width="600px"
            >
                <v-card
                    :loading="isSaving"
                >
                    <template
                        v-slot:progress
                    >
                        <v-progress-linear
                            color="primary"
                            height="10"
                            indeterminate
                        />
                    </template>
                    <v-card-title>
                        <span
                            class="headline"
                            v-if="!newRula.id"
                        >
                            {{ $t('rula.add') }}
                        </span>
                        <span
                            class="headline"
                            v-else
                        >
                            {{ $t('rula.edit') }}
                        </span>
                    </v-card-title>
                    <v-card-text>
                        <v-form>
                            <v-row>
                                <v-col>
                                    <upload
                                        v-model="multiple"
                                        ref="multipleForm"
                                        :multiple="true"
                                        :label="$t('rula.label.file')"
                                    />
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-card-text>
                    <v-card-actions>
                        <v-btn
                            text
                            @click="closeMultipleDialog"
                            :disabled="isSaving"
                        >
                            {{ $t('action.cancel') }}
                        </v-btn>
                        <v-spacer/>
                        <v-btn
                            color="secondary"
                            @click="addMultiple"
                            :disabled="isSaving"
                        >
                            {{ $t('action.add') }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
            <v-dialog
                v-model="newDialog"
                persistent
                max-width="600px"
            >
                <v-card
                    :loading="isSaving"
                >
                    <template
                        v-slot:progress
                    >
                        <v-progress-linear
                            color="primary"
                            height="10"
                            indeterminate
                        />
                    </template>
                    <v-card-title>
                        <span
                            class="headline"
                            v-if="!newRula.id"
                        >
                            {{ $t('rula.add') }}
                        </span>
                        <span
                            class="headline"
                            v-else
                        >
                            {{ $t('rula.edit') }}
                        </span>
                    </v-card-title>
                    <v-card-text>
                        <v-form>
                            <v-row>
                                <v-col>
                                    <v-alert
                                        border="left"
                                        type="info"
                                    >
                                        {{ $t('rula.info.fileOrUrl') }}
                                    </v-alert>
                                    <upload
                                        v-model="newRula._file"
                                        ref="uploadForm"
                                        :label="$t('rula.label.file')"
                                    />
                                    <div
                                        class="v-text-field__details mt-4"
                                    >
                                        <div
                                            class="v-messages theme--light error--text"
                                            role="alert"
                                        >
                                            <div
                                                class="v-messages__wrapper"
                                            >
                                                <div
                                                    class="v-messages__message"
                                                    v-for="(error, key) in fileErrors"
                                                    :key="key"
                                                >
                                                    {{ error }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col>
                                    <v-text-field
                                        v-model="newRula.url"
                                        :label="$t('rula.label.url')"
                                        placeholder="https://www.example.com"
                                        :hint="$t('hint.url')"
                                        :error-messages="urlErrors"
                                        @input="$v.newRula.url.$touch()"
                                        @blur="$v.newRula.url.$touch()"
                                    />
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-card-text>
                    <v-card-actions>
                        <v-btn
                            text
                            @click="closeNewDialog"
                            :disabled="isSaving"
                        >
                            {{ $t('action.cancel') }}
                        </v-btn>
                        <v-spacer/>
                        <v-btn
                            color="secondary"
                            @click="addRula"
                            :disabled="isSaving"
                        >
                            {{ $t('action.add') }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-card-text>
    </v-card>
</template>

<script>
import Upload from "@/components/Upload/Upload";
import {validationMixin} from "vuelidate";
import {required, requiredIf, url} from "vuelidate/lib/validators";
import axios from "axios";
import {SnackBarAction, SnackBarTypes} from "@/store/snackbar/types";
import {mapActions} from "vuex";

export default {
    mixins: [
        validationMixin,
    ],
    components: {
        Upload,
    },
    props: {
        category: {
            type: Object,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        canEdit: {
            type: Boolean,
            default: false,
        },
        categories: {
            type: Array,
            required: true,
        },
        booking: {
            type: Object,
            required: true,
        },
    },
    data: () => ({
        isSaving: false,
        newDialog: false,
        newRula: {
            id: null,
            category: null,
            _file: null,
            url: null,
        },
        fab: false,
        multipleDialog: false,
        multiple: [],
    }),
    mounted() {
        this.newRula.category = this.category.category;
    },
    validations: {
        newRula: {
            category: {
                required,
            },
            _file: {
                required: requiredIf(function (nestedModel) {
                    return !nestedModel.url;
                }),
            },
            url: {
                required: requiredIf(function (nestedModel) {
                    return !nestedModel._file;
                }),
                url,
            },
        }
    },
    computed: {
        onSite: {
            get: function () {
                return this.category.onSite;
            },
            set: function (value) {
                this.$emit('updateOnSite', {
                    onSite: value,
                    category: this.category,
                });
            },
        },
        notAvailable: {
            get: function () {
                return this.category.notAvailable;
            },
            set: function (value) {
                this.$emit('updateNotAvailable', {
                    notAvailable: value,
                    category: this.category,
                });
            },
        },
        fileHeaders() {
            return [
                {
                    text: this.$t('upload.header.fileOrUrl'),
                    value: 'fileOrUrl',
                },
                {
                    text: this.$t('action.actions'),
                    value: '_actions',
                },
            ];
        },
        fileErrors() {
            const errors = [];
            if(!this.$v.newRula._file.$dirty) {
                return errors;
            }
            if(!this.$v.newRula._file.required) {
                errors.push(this.$t('errors.required', { field: this.$t('rula.label.file')}));
            }

            return errors;
        },
        urlErrors() {
            const errors = [];
            if(!this.$v.newRula.url.$dirty) {
                return errors;
            }
            if(!this.$v.newRula.url.required) {
                errors.push(this.$t('errors.required', { field: this.$t('rula.label.url')}));
            }
            if(!this.$v.newRula.url.url) {
                errors.push(this.$t('errors.url', { field: this.$t('rula.label.url')}));
            }

            return errors;
        },
    },
    methods: {
        ...mapActions({
            setSnackbar: `snackbar/${SnackBarAction.SET}`,
        }),
        addMultiple() {

            if(
                this.multiple &&
                this.multiple.length > 0
            ) {
                this.isSaving = true;

                let formData = new FormData();

                this.multiple.forEach((file) => {
                    formData.append('_files[]', file);
                });

                formData.append('category', this.category.category);

                axios.post(
                    `/booking/${this.booking.booking_no}/rula/${this.newRula.category}/multiple`,
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                )
                    .then(resp => {
                        return resp.data;
                    })
                    .then(data => {
                        this.$emit('change', data);

                        this.closeMultipleDialog();
                    })
                    .catch(err => {
                        this.setSnackbar(
                            this.$t('errors.save'),
                            SnackBarTypes.ERROR
                        );
                        console.error(err);
                    })
                    .finally(() => {
                        this.isSaving = false;
                    });
            }
        },
        addRula() {
            this.$v.newRula.$touch();
            if(
                !this.$v.newRula.$invalid
            ) {
                this.isSaving = true;

                let formData = new FormData();

                const assetFields = [
                    '_file',
                ];

                for (let i = 0, l = assetFields.length; i < l; i++) {
                    const field = assetFields[i];
                    if(
                        Object.prototype.hasOwnProperty.call(this.newRula, field) &&
                        this.newRula[field]
                    ) {
                        formData.append(field, this.newRula[field][0]);
                    }
                }

                for(let i in this.newRula) {
                    if(
                        false === Object.prototype.hasOwnProperty.call(this.newRula, i) ||
                        assetFields.indexOf(i) > -1
                    ) {
                        continue;
                    }
                    formData.append(i, this.newRula[i]);
                }

                const category = this.getCategory(this.newRula.category);
                let index = 0;
                if(
                    category
                ) {
                    index = category.files.length;
                }
                formData.append('_index', index.toString());

                axios.post(
                    `/booking/${this.booking.booking_no}/rula/${this.newRula.category}`,
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                )
                    .then(resp => {
                        return resp.data;
                    })
                    .then(data => {
                        this.$emit('change', data);

                        this.closeNewDialog();
                    })
                    .catch(err => {
                        this.setSnackbar(
                            this.$t('errors.save'),
                            SnackBarTypes.ERROR
                        );
                        console.error(err);
                    })
                    .finally(() => {
                        this.isSaving = false;
                    });
            }
        },
        deleteItem(item) {
            this.$emit('deleteAsset', {
                id: item.id,
                category: this.category,
            });
        },
        closeNewDialog() {
            this.newDialog = false;
            this.$refs.uploadForm.reset();
            this.$v.newRula.$reset()
            this.newRula = {
                id: null,
                category: null,
                _file: null,
                url: null,
            };
        },
        closeMultipleDialog() {
            this.multipleDialog = false;
            this.$refs.multipleForm.reset();
            this.multiple = [];
        },
        getCategory(category) {
            for(let i = 0, l = this.categories.length; i < l; i++) {
                if(this.categories[i].category === category) {
                    return this.categories[i];
                }
            }

            return null;
        },
    },
}
</script>

<style lang="scss">
.v-speed-dial {
    bottom: -28px;
}
</style>
